import { FloorplanData } from './components/floorplan/floorplan'

export interface LangStrings {
    timer_label: string
    session_expired: string
    floorplan_screen: string
    floorplan_entry: string
    floorplan_stage: string
    floorplan_balcony: string
    floorplan_sideBalcony: string
    intro_freeSeats: string
    tickets_title: string
    tickets_max: string
    tickets_serialTicket: string
    tickets_giftCard: string
    tickets_amountLeft: string
    tickets_total: string
    products_title: string
    cart_title: string
    cards_title: string
    cards_label: string
    cards_button: string
    cards_notFound: string
    cards_alreadyAdded: string
    cards_notApplicable: string
    contact_title: string
    contact_description: string
    contact_name: string
    contact_name_invalid: string
    contact_email: string
    contact_email_invalid: string
    contact_email_card_mismatch: string
    contact_phone: string
    contact_newsletter: string
    terms: string
    banks_title: string
    banks_paymentFailed: string
    banks_required: string
    error_title: string
    submit_register: string
    submit_pay: string
    schedule: string
    completed_title: string
    completed_startNew: string
    soldOut_title: string
    soldOut_text: string
    temporarilyUnavailable_title: string
    pending_title: string
    pending_text: string
    sale_expired: string
    giftCard_amountPresets_text: string
    giftCard_oneTicket: string
    giftCard_twoTickets: string
    giftCard_sixTickets: string
    giftCard_tenTickets: string
    giftCard_customAmount: string
    giftCard_amount_invalid: string
    giftCard_amount_min: string
    giftCard_amount_max: string
    giftCard_email_label: string
    giftCard_completed_title: string
    giftCard_code: string
    giftCard_usage: string
    serialTicket_type_label: string
    serialTicket_type_uses: string
    serialTicket_type_expires: string
    serialTicket_email_label: string
    serialTicket_completed_title: string
    serialTicket_code: string
    serialTicket_usage: string
    serialTicket_amountLeft_multiple: string
    serialTicket_amountLeft_singular: string
    progressBar_tickets: string
    progressBar_products: string
    progressBar_summary: string
    chooseProducts: string
    completePurchase: string
    back: string
    allProducts: string
    searchProduct: string
    uncategorizedProducts: string
    myTickets: string
    change: string
    products_noResults: string
    products_tryAnotherSearch: string
    products_removeFilters: string
    products_disabled: string
    products_total: string
    products_sidebar_title: string
    products_sidebar_firstParagraph: string
    products_sidebar_textBeforeLink: string
    products_sidebar_linkText: string
    products_sidebar_textAfterLink: string
    eventSelection_chooseEventTitle: string
    eventSelection_chooseSeatTitle: string
    eventSelection_chooseProduction: string
    eventSelection_chooseEvent: string
    eventSelection_chooseRow: string
    eventSelection_chooseSeat: string
    eventSelection_noNumberedSeats: string
    row: string
    seat: string
    cartEmpty: string
}

export type PluginTheme = 'dark' | 'light'

type EventStatus =
    | 'checkout'
    | 'completed'
    | 'sold-out'
    | 'temporarily-unavailable'
    | 'payment-pending'
    | 'sale-expired'

type GiftCardStatus = 'checkout' | 'completed' | 'payment-pending'
type SerialTicketStatus = 'checkout' | 'completed' | 'payment-pending'

export enum CheckoutStep {
    TICKETS = 'tickets',
    PRODUCTS = 'products',
    SUMMARY = 'summary',
}

export interface ImageData {
    srcset: string
    alt?: string
}

interface ProductionData {
    id: string
    image: ImageData
    name: string
}

interface DetailsData {
    startDate: string
    datetime: {
        date: string
        time: string
    }
    additionalInfo?: string
}

export interface Labels {
    intro: {
        title: string
        text: string
        venueName: string
    }
    tickets: {
        subtitle: string
    }
    productSelection: string
    paySection: string
}

interface EventCompletionData {
    ticketsTitle: string
    ticketLines: string[]
    ticketsSentText: string
    ticketsDownloadText: string
    ticketsButton: {
        url: string
        text: string
    }
    invoiceSentText: string
    invoiceDownloadText: string
    invoiceButton: {
        url: string
        text: string
    }
    contactInfo: string
}

interface GiftCardCompletionData {
    cardCode: string
    emailSentText: string
    contactInfo: string
    downloadButton: {
        url: string
        text: string
    }
}

interface SerialTicketCompletionData {
    ticketCode: string
    emailSentText: string
    contactInfo: string
    downloadButton: {
        url: string
        text: string
    }
}

export interface TicketType {
    id: string
    name: string
    /** Unit price */
    price: number
}

export interface UnavailableSeat {
    row: number
    seat: number
    status: 'disabled' | 'taken'
}

interface Seats {
    freeCount?: number
    unavailable: UnavailableSeat[]
}

export interface EventData {
    eventUuid: string
    eventStartTime: string
    productionUuid: string
    productionName: string
    venueName?: string
    roomName?: string
    maxRow?: number
    maxSeat?: number
}

// Keep this in sync with the matching type in the kinola repo
export interface OrderCalculation {
    timestamp: number
    tickets: TicketGroup[]
    products: ProductVariantSummary[]
    serialTicketPayments: SerialTicketPayment[]
    memberCard?: AppliedMemberCard
    giftCardPayments: GiftCardPayment[]
    total: number
    partialPayments: PartialPayment[]
    toPay: number
}

// Keep this in sync with the matching type in the kinola repo
export interface TicketGroup {
    typeId: number
    typeUuid: string
    name: string
    quantity: number
    unitPrice: number
    seats?: TicketSeat[]
}

// Keep this in sync with the matching type in the kinola repo
export interface TicketSeat {
    rowNumber: number
    seatNumber: number
    name: string
    allowedTicketTypeIds: string[]
}

// Keep this in sync with the matching type in the kinola repo
export interface ProductVariantSummary {
    name: string
    quantity: number
    unitPrice: number
}

// Keep this in sync with the matching type in the kinola repo
export interface SerialTicketPayment {
    serialTicketId: number
    number: string
    amount: number
    remaining: number
}

// Keep this in sync with the matching type in the kinola repo
export interface AppliedMemberCard {
    id: number
    number: string
    discount: number
    name: string
    description?: string
    member: CustomerData
}

// Keep this in sync with the matching type in the kinola repo
export interface GiftCardPayment {
    giftCardId: number
    number: string
    amount: number
    amountLeft: number
    member?: CustomerData
}

// Keep this in sync with the matching type in the kinola repo
type PaymentMethod = 'epassi' // More methods may be added later

// Keep this in sync with the matching type in the kinola repo
interface PartialPayment {
    method: PaymentMethod
    label: string
    amount: number
    // For methods that allow more than one payment, we may
    // add an additional identifier later
}

// Keep this in sync with the matching type in the kinola repo
export interface CustomerData {
    name: string
    email: string
    phone: string | null
}

export interface CustomerDataWithPreferences extends CustomerData {
    newsletter: boolean
}

export interface PartialEvent {
    calc: OrderCalculation
    ticketTypes: TicketType[]
    seats: Seats
    status: EventStatus
    paymentFailed: boolean
    paymentOptions?: PaymentOption[]
    completionData?: EventCompletionData
    gtm?: Array<unknown>
    products: Product[]
}

export interface Event extends PartialEvent {
    id: string
    details: DetailsData
    production: ProductionData
    productCategories: ProductCategory[]
    floorplan?: FloorplanData
    labels: Labels
    sessionMinutes: number
    hideDiscountCards: boolean
    externalTicketUrl?: string
    isProductSaleEnabled: boolean
}

export interface PluginSession {
    eventUuid: string
    rowNumber?: number
    seatNumber?: number
}

export interface GeneralData {
    isProductSaleEnabled: boolean
}

export interface PaymentOption {
    name: string
    label: string
    debug?: string
}

export type ValidationError = 'required' | 'invalid' | 'min' | 'max' | 'card-mismatch'

export type PaymentGatewayValidationErrors = Record<string, string>

export interface ValidationErrors {
    name?: ValidationError
    email?: ValidationError
    phone?: ValidationError
    newsletter?: ValidationError
    amount?: ValidationError
    gateway?: PaymentGatewayValidationErrors
}

export interface GiftCardData {
    ticketPrice: number
    minAmount: number
    labels: {
        title: string
        note: string
        emailNote: string
    }
    status: GiftCardStatus
    paymentFailed: boolean
    completionData?: GiftCardCompletionData
    showAmountPresetButtons: boolean
    paymentOptions: PaymentOption[]
}

export interface SerialTicketData {
    labels: {
        title: string
        note: string
    }
    status: SerialTicketStatus
    types: SerialTicketType[]
    paymentFailed: boolean
    completionData?: SerialTicketCompletionData
    paymentOptions: PaymentOption[]
}

interface SerialTicketType {
    id: number
    name: string
    price: number
    uses: number
    expiration: string // DD.MM.YYYY
    description?: string
}

interface EventSubmitCompleted {
    status: 'completed'
    data: EventCompletionData
}

interface EventSubmitPending {
    status: 'payment-pending'
    redirectUrl: string
}

export interface EventSubmitError {
    status: 'checkout'
    validationErrors: ValidationErrors
}

export type EventSubmitResult = EventSubmitCompleted | EventSubmitPending | EventSubmitError

interface GiftCardSubmitPending {
    status: 'payment-pending'
    redirectUrl: string
}

export interface GiftCardSubmitError {
    status: 'checkout'
    validationErrors?: ValidationErrors
    paymentFailed?: boolean
}

export type GiftCardSubmitResult = GiftCardSubmitPending | GiftCardSubmitError

interface SerialTicketSubmitPending {
    status: 'payment-pending'
    redirectUrl: string
}

export interface SerialTicketSubmitError {
    status: 'checkout'
    validationErrors?: ValidationErrors
    paymentFailed?: boolean
}

export type SerialTicketSubmitResult = SerialTicketSubmitPending | SerialTicketSubmitError

export interface EventPaymentGatewayValidationError {
    status: 'checkout'
    validationErrors: ValidationErrors
}

export interface EventPaymentGatewayValidationCompleted {
    status: 'checkout'
    gatewayValid: true
}

export type EventPaymentGatewayValidationResult =
    | EventPaymentGatewayValidationError
    | EventPaymentGatewayValidationCompleted

export interface SessionUpdateResult {
    success: true
}

export interface ProductCategory {
    id: number
    name: string
}

export interface ProductVariant {
    id: number
    name: string
    unitPrice: number // TODO remove
    quantity: number
    maxQuantity?: number
}

export interface Product {
    id: number
    name: string
    description: string
    image: ImageData
    variants: ProductVariant[]
    categories: number[]
}

export interface CommonState {
    isLoading?: boolean
    theme: PluginTheme
    lang: LangStrings
    validationErrors: ValidationErrors
}

export interface EventState extends CommonState {
    session?: PluginSession
    eventUuid?: string
    selectedLang: string
    eventsData?: EventData[]
    selectedTicket: {
        productionUuid?: string
        eventUuid?: string
        row?: number
        seat?: number
    }
    isAddProductsFlow: boolean
    isInitializing: boolean
    hideDiscountCards: boolean
    sessionMinutes: number
    sessionEndTime: number
    details: DetailsData
    production: ProductionData
    floorplan?: FloorplanData
    hasNewsletterCheckbox: boolean
    newsletterCheckedByDefault: boolean
    termsLink: string
    labels: Labels
    status: EventStatus
    paymentOptions: PaymentOption[]
    isProductSaleEnabled?: boolean
    activeCheckoutStep?: CheckoutStep
    productCategories?: Map<number, ProductCategory>
    products: Product[]
    productsPageNumber: number
    selectedCategory?: string
    productSearch?: string
    paymentFailed: boolean
    scheduleUrl: string
    completionData: EventCompletionData
    ticketTypes: TicketType[]
    calc: OrderCalculation
    seats: Seats
    isTicketLimit?: boolean
    cards: {
        code: string
        notFound?: boolean
        alreadyAdded?: boolean
        notApplicable?: boolean
    }
    isMyTicketsSectionOpen?: boolean
}

export interface GiftCardState extends CommonState {
    data?: GiftCardData
    amount: string
    email: string
}

export interface SerialTicketState extends CommonState {
    data?: SerialTicketData
    typeId: number
    email: string
}

interface View<State> {
    state: State
    update: () => void
}

export interface EventView extends View<EventState> {
    timeouts: Map<string, number>
}

export type GiftCardView = View<GiftCardState>
export type SerialTicketView = View<SerialTicketState>

export interface Api {
    events: EventApi
    giftCards: GiftCardApi
    serialTickets: SerialTicketApi
    session: SessionApi
    getGeneralData: () => Promise<GeneralData>
}

export interface EventApi {
    getEvent: (eventId: string, lang: string) => Promise<Event>
    addSeat: (
        eventId: string,
        lang: string,
        rowNumber: number,
        seatNumber: number,
    ) => Promise<PartialEvent>
    setProductVariantQuantity: (
        eventId: string,
        lang: string,
        variantId: number,
        quantity: number,
    ) => Promise<PartialEvent>
    removeSeat: (
        eventId: string,
        lang: string,
        rowNumber: number,
        seatNumber: number,
    ) => Promise<PartialEvent>
    updateSeat: (
        eventId: string,
        lang: string,
        rowNumber: number,
        seatNumber: number,
        ticketTypeId: string,
    ) => Promise<PartialEvent>
    setTicketAmount: (
        eventId: string,
        lang: string,
        ticketTypeId: string,
        amount: number,
    ) => Promise<PartialEvent>
    applyDiscount: (eventId: string, lang: string, code: string) => Promise<PartialEvent>
    removeCard: (eventId: string, lang: string, code: string) => Promise<PartialEvent>
    removeSerialTicket: (
        eventId: string,
        lang: string,
        serialTicketId: number,
    ) => Promise<PartialEvent>
    submit: (
        eventId: string,
        customer: CustomerData,
        selectedPaymentGateway: string,
        returnUrl: string,
    ) => Promise<EventSubmitResult>
    validateSelectedPaymentGateway: (
        eventId: string,
        customer: CustomerData,
        selectedPaymentGateway: string,
    ) => Promise<EventPaymentGatewayValidationResult>
    reset: (eventId: string) => Promise<void>
    getEvents: () => Promise<EventData[]>
}

export interface GiftCardApi {
    getData: () => Promise<GiftCardData>
    submit: (
        amount: string,
        email: string,
        returnUrl: string,
        selectedPaymentGateway: string,
    ) => Promise<GiftCardSubmitResult>
    reset: () => Promise<void>
}

export interface SerialTicketApi {
    getData: () => Promise<SerialTicketData>
    submit: (
        typeId: number,
        email: string,
        returnUrl: string,
        selectedPaymentGateway: string,
    ) => Promise<SerialTicketSubmitResult>
    reset: () => Promise<void>
}

export interface SessionApi {
    getSession: () => Promise<PluginSession | null>
    updateSession: (session: PluginSession) => Promise<SessionUpdateResult>
}
